import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import mapicon from "../../../assets/Dashboard/mapicon.svg";
import empty from "../../../assets/empty.png";
import { useTranslation } from "react-i18next";
import ReportService from "../../../services/reportService";

const WorkerScheduleCompliance = ({ dateRange }) => {
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.company_id;
  const workerId = user?.id;
  const { t } = useTranslation();

  const [reportData, setReportData] = useState({});
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) return;
    fetchReport(dateRange.startDate, dateRange.endDate);
  }, [dateRange, companyId, workerId]);

  // Company-wide requests return one summary row per worker — sum them into a single
  // period-summary shape so the rest of the component (chart + modal) stays unchanged.
  const aggregateWorkerRows = (rows) => {
    if (!rows.length) return {};
    const totals = rows.reduce(
      (acc, row) => {
        acc.total_scheduled_days += parseInt(row.total_scheduled_days) || 0;
        acc.days_attended += parseInt(row.days_attended) || 0;
        acc.days_absent += parseInt(row.days_absent) || 0;
        acc.total_scheduled_hours += parseFloat(row.total_scheduled_hours) || 0;
        acc.total_actual_hours += parseFloat(row.total_actual_hours) || 0;
        return acc;
      },
      { total_scheduled_days: 0, days_attended: 0, days_absent: 0, total_scheduled_hours: 0, total_actual_hours: 0 }
    );
    return {
      ...totals,
      total_scheduled_hours: Number(totals.total_scheduled_hours.toFixed(2)),
      total_actual_hours: Number(totals.total_actual_hours.toFixed(2)),
      overall_compliance_percentage: totals.total_scheduled_hours
        ? Number(((totals.total_actual_hours / totals.total_scheduled_hours) * 100).toFixed(2))
        : 0,
      attendance_rate_percentage: totals.total_scheduled_days
        ? Number(((totals.days_attended / totals.total_scheduled_days) * 100).toFixed(2))
        : 0,
    };
  };

  const fetchReport = async (start, end) => {
    if (!companyId || !workerId) return;
    setLoading(true);
    try {
      const result = await ReportService.getScheduleComplianceReport(start, end, workerId, companyId);

      if (!result.error && result.data?.data?.length > 0) {
        setReportData(aggregateWorkerRows(result.data.data));
      } else {
        setReportData({});
      }
    } catch (e) {
      console.error("Error fetching schedule compliance:", e);
      setReportData({});
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  const attendanceData = [
    {
      name: t("reportsScreen.attendanceSummary"),
      [t("reportsScreen.totalDays")]: parseInt(reportData.total_scheduled_days) || 0,
      [t("reportsScreen.daysAttended")]: parseInt(reportData.days_attended) || 0,
      [t("reportsScreen.daysAbsent")]: parseInt(reportData.days_absent) || 0,
    },
  ];

  const hoursData = [
    {
      name: t("reportsScreen.attendanceSummary"),
      [t("reportsScreen.scheduledHours")]: parseFloat(reportData.total_scheduled_hours) || 0,
      [t("reportsScreen.actualHours")]: parseFloat(reportData.total_actual_hours) || 0,
    },
  ];

  const hasData = attendanceData[0][t("reportsScreen.totalDays")] > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("reportsScreen.employeeScheduleComplianceReport")}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#6B7280",
            fontFamily: "Poppins, sans-serif",
            mt: 0.5,
          }}
        >
            {t("reportsScreen.scheduleComplianceApiDesc")
    .split(",")
    .map(item =>
      item
        .trim()
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase())
    )
    .join(", ")}
        </Typography>
      </Box>

      {/* Chart */}
      <Card
        sx={{
          p: 2,
          border: "2px solid #091E4224",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box>
<Typography
                variant="h6"
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.scheduleVsActualAttendance")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9CA3AF",
                  fontFamily: "Poppins, sans-serif",
                  mt: 0.3,
                }}
              >
                {t("reportsScreen.totalDays")}, {t("reportsScreen.daysAttended")}, {t("reportsScreen.daysAbsent")}, {t("reportsScreen.scheduledHours")}, {t("reportsScreen.actualHours")}
              </Typography>
          </Box>
          <IconButton onClick={() => setOpenSummaryModal(true)}>
            <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
          </IconButton>
        </Box>

        {hasData ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[{ ...attendanceData[0], ...hoursData[0] }]} barCategoryGap="35%" barGap={10}>
              <XAxis dataKey="name" />
              <YAxis yAxisId="days" />
              <YAxis yAxisId="hours" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="days" dataKey={t("reportsScreen.totalDays")} fill="#42A5F5" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="days" dataKey={t("reportsScreen.daysAttended")} fill="#66BB6A" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="days" dataKey={t("reportsScreen.daysAbsent")} fill="#EF5350" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="hours" dataKey={t("reportsScreen.scheduledHours")} fill="#AB47BC" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="hours" dataKey={t("reportsScreen.actualHours")} fill="#FFA726" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box height={300} display="flex" alignItems="center" justifyContent="center">
            <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
          </Box>
        )}
      </Card>

      {/* Summary Modal */}
      <Modal open={openSummaryModal} onClose={() => setOpenSummaryModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: 500,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#003149", fontFamily: "Poppins, sans-serif" }}
            >
              {t("reportsScreen.summaryData")}
            </Typography>
            <IconButton onClick={() => setOpenSummaryModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {Object.keys(reportData).length ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th style={{ padding: "10px", textAlign: "left" }}>{t("reportsScreen.metric")}</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>{t("reportsScreen.value")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reportData).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                      {t(`summaryLabels.${k}`, k.replace(/_/g, " "))}
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #eee", textAlign: "right" }}>
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Typography sx={{ textAlign: "center" }}>{t("reportsScreen.noData")}</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default WorkerScheduleCompliance;
